import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    DataGridBody,
    DataGridRow,
    DataGrid,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridCell,
    TableCellLayout,
    createTableColumn,
    Button,
    Tooltip,
    SearchBox,
  } from "@fluentui/react-components";
import { DeleteRegular, CircleRegular, SettingsRegular, DocumentCheckmarkRegular, CheckmarkCircleRegular, ArrowUndoRegular } from '@fluentui/react-icons';
import { getMatchedSyncStatus, getPercentageColor, formatDate } from '../utils/syncStatusHelpers';
import { includesIgnoreCase } from '../utils/stringHelpers';

const buttonStyles = `
  .features-button,
  .licenses-button,
  .verify-button,
  .undelete-button,
  .delete-button {
    border-radius: 4px;
    padding: 8px 16px;
    min-width: 48px;
    width: auto;
    transition: all 0.2s ease;
    color: white;
  }
  .features-button,
  .verify-button {
    background-color: #107c10;
    border-color: #107c10;
  }
  .features-button:hover:not(:disabled),
  .verify-button:hover:not(:disabled) {
    background-color: #0b6e0b;
    border-color: #0b6e0b;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(16, 124, 16, 0.3);
  }
  .licenses-button {
    background-color: #8764b8;
    border-color: #8764b8;
  }
  .licenses-button:hover:not(:disabled) {
    background-color: #5c2e91;
    border-color: #5c2e91;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(135, 100, 184, 0.3);
  }
  .undelete-button {
    background-color: #0078d4;
    border-color: #0078d4;
  }
  .undelete-button:hover:not(:disabled) {
    background-color: #006cbe;
    border-color: #006cbe;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 120, 212, 0.3);
  }
  .delete-button {
    background-color: #d13438;
    border-color: #d13438;
  }
  .delete-button:hover:not(:disabled) {
    background-color: #c02a2f;
    border-color: #c02a2f;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(209, 52, 56, 0.3);
  }
  .features-button:disabled,
  .licenses-button:disabled,
  .verify-button:disabled,
  .undelete-button:disabled,
  .delete-button:disabled {
    background-color: #a19f9d;
    border-color: #a19f9d;
    cursor: not-allowed;
  }
  .features-button svg,
  .licenses-button svg,
  .verify-button svg,
  .undelete-button svg,
  .delete-button svg {
    color: white;
  }
  [role="row"] {
    min-height: 32px;
    height: auto;
  }
  [role="gridcell"],
  [role="columnheader"] {
    padding: 2px 4px;
    vertical-align: middle;
  }
  .fui-Tooltip,
  [class*="Tooltip"],
  [data-tooltip] {
    background-color: #323130;
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
  .search-box-underline {
    border: 2px solid #605e5c;
  }
  .search-box-underline:focus-within {
    border-color: #0078d4;
    border-width: 3px;
  }
  .search-box-underline input {
    font-weight: 400;
    color: #605e5c;
  }
  .search-box-underline input::placeholder {
    color: #8a8886;
  }
  .non-clickable-row:hover {
    background-color: transparent !important;
  }
`;

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [syncStatuses, setSyncStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const fetchCustomers = useCallback(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers`)
      .then((res) => res.json())
      .then((data) => {
        const globalSyncsCustomer = {
          CustomerID: 9999999,
          TenantInt: 9999999,
          Email: "-",
          IsVerified: 0,
          IsDeleted: 0,
          CreationDate: null,
          CompanyName: "All Tenants Syncs - these syncs impact all customers",
          TenantName: "Global - these syncs impact all customers"
        };
        data = [globalSyncsCustomer, ...data];
        setCustomers(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch customers:', err);
        setLoading(false);
      });
  }, []);

  const fetchSyncStatuses = useCallback(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/syncs/statuses`)
      .then((res) => res.json())
      .then((data) => {
        const statusMap = {};
        data.forEach((entry) => {
          statusMap[entry.TenantInt] = entry;
        });
        setSyncStatuses(statusMap);
      })
      .catch((err) => {
        console.error('Failed to fetch sync statuses:', err);
      });
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchSyncStatuses();
  }, [fetchCustomers, fetchSyncStatuses]);

  const handleDeleteCustomer = (customerId) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}`, {
      method: 'DELETE',
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to delete customer');
        }
        return res.json();
      })
      .then(() => {
        alert('Customer deleted successfully');
        fetchCustomers();
      })
      .catch((err) => {
        console.error(err);
        alert('Error deleting customer.');
      });
  };

  const handleVerifyCustomer = (customerId) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}/verify`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to verify customer');
        }
        return res.json();
      })
      .then(() => {
        alert('Customer verified successfully');
        fetchCustomers();
      })
      .catch((err) => {
        console.error(err);
        alert('Error verifying customer.');
      });
  };

  const handleUndeleteCustomer = (customerId) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}/undelete`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to restore customer');
        }
        return res.json();
      })
      .then(() => {
        alert('Customer restored successfully');
        fetchCustomers();
      })
      .catch((err) => {
        console.error(err);
        alert('Error restoring customer.');
      });
  };

  const MatchTenantStatuses = (tenantId, syncStatusesMap) => {
    if (!tenantId) {
      return {
        statusIcon: <CircleRegular style={{ color: '#999', fontSize: '32px' }} />,
        tooltipText: 'Tenant connectors are not configured for this customer',
        tooltipRatio: null
      };
    }
    const syncInfo = syncStatusesMap[tenantId];
    return getMatchedSyncStatus(syncInfo);
  };

  const filteredCustomers = useMemo(() => {
    if (!searchTerm.trim()) {
      return customers;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return customers.filter(customer =>
      includesIgnoreCase(customer.CompanyName, lowerSearchTerm) ||
      includesIgnoreCase(customer.Email, lowerSearchTerm)
    );
  }, [customers, searchTerm]);

  const handleSearchChange = (e, data) => {
    setSearchTerm(data.value || '');
  };

  const columns = [
    createTableColumn({
      columnId: 'CompanyName',
      compare: (a, b) => {
        return (a.CompanyName || '').localeCompare(b.CompanyName || '');
      },
      renderHeaderCell: () => 'Company Name',
      renderCell: (item) => (
        <TableCellLayout>
          {item.CompanyName || '-'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'Email',
      compare: (a, b) => {
        return (a.Email || '').localeCompare(b.Email || '');
      },
      renderHeaderCell: () => 'Email',
      renderCell: (item) => (
        <TableCellLayout>
          {item.Email || '-'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'IsVerified',
      compare: (a, b) => {
        return (a.IsVerified ? 1 : 0) - (b.IsVerified ? 1 : 0);
      },
      renderHeaderCell: () => 'Is Verified',
      renderCell: (item) => (
        <TableCellLayout>
          {item.IsVerified ? 'Yes' : 'No'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'IsDeleted',
      compare: (a, b) => {
        return (a.IsDeleted ? 1 : 0) - (b.IsDeleted ? 1 : 0);
      },
      renderHeaderCell: () => 'Is Deleted',
      renderCell: (item) => (
        <TableCellLayout>
          {item.IsDeleted ? 'Yes' : 'No'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'CreationDate',
      compare: (a, b) => {
        const dateA = a.CreationDate ? new Date(a.CreationDate) : new Date(0);
        const dateB = b.CreationDate ? new Date(b.CreationDate) : new Date(0);
        return dateA - dateB;
      },
      renderHeaderCell: () => 'Creation Date',
      renderCell: (item) => (
        <TableCellLayout>
          {formatDate(item.CreationDate)}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'Status',
      compare: () => 0,
      renderHeaderCell: () => 'Status',
      renderCell: (item) => {
        const { statusIcon, tooltipText, tooltipRatio, percentage } = MatchTenantStatuses(item.TenantInt, syncStatuses);
        const percentageColor = item.TenantInt ? getPercentageColor(percentage) : null;

        return (
          <TableCellLayout className="status-column-cell">
            <Tooltip 
              relationship="description"
              content={
                <div style={{ 
                  backgroundColor: '#323130', 
                  color: 'white', 
                  padding: '8px 12px', 
                  borderRadius: '4px',
                  fontSize: '14px',
                  maxWidth: '300px'
                }}>
                  {tooltipText}
                  {tooltipRatio && (
                    <>
                      <br />
                      {tooltipRatio}
                    </>
                  )}
                </div>
              }
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'default' }}>
                <span>{statusIcon}</span>
                {percentage != null && (
                  <span style={{ color: percentageColor, fontWeight: '500', fontSize: '14px' }}>
                    {percentage}%
                  </span>
                )}
              </div>
            </Tooltip>
          </TableCellLayout>
        );
      }
    }),
    createTableColumn({
      columnId: 'Features',
      compare: () => 0,
      renderHeaderCell: () => 'Features',
      renderCell: (item) => {
        const isGlobalSyncs = item.TenantInt === 9999999;
        return (
          <TableCellLayout>
            <Tooltip
              relationship="description"
              content={
                <div style={{
                  backgroundColor: '#323130',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}>
                  {isGlobalSyncs ? 'Not available for global syncs' : 'Features'}
                </div>
              }
            >
              <Button
                className="features-button"
                icon={<SettingsRegular />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/features/${item.CustomerID}`);
                }}
                aria-label="Features"
                disabled={isGlobalSyncs}
              />
            </Tooltip>
          </TableCellLayout>
        );
      }
    }),
    createTableColumn({
      columnId: 'Licenses',
      compare: () => 0,
      renderHeaderCell: () => 'Licenses',
      renderCell: (item) => {
        const isGlobalSyncs = item.TenantInt === 9999999;
        return (
          <TableCellLayout>
            <Tooltip
              relationship="description"
              content={
                <div style={{
                  backgroundColor: '#323130',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}>
                  {isGlobalSyncs ? 'Not available for global syncs' : 'Licenses'}
                </div>
              }
            >
              <Button
                className="licenses-button"
                icon={<DocumentCheckmarkRegular />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/licenses/${item.CustomerID}`);
                }}
                aria-label="Licenses"
                disabled={isGlobalSyncs}
              />
            </Tooltip>
          </TableCellLayout>
        );
      }
    }),
    createTableColumn({
      columnId: 'Actions',
      compare: () => 0,
      renderHeaderCell: () => 'Actions',
      renderCell: (item) => {
        const isGlobalSyncs = item.TenantInt === 9999999;
        const isDeleted = item.IsDeleted === 1 || item.IsDeleted === true;
        const isVerified = item.IsVerified === 1 || item.IsVerified === true;
        
        return (
          <TableCellLayout>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Tooltip
                relationship="description"
                content={
                  <div style={{
                    backgroundColor: '#323130',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    {isGlobalSyncs ? 'Not available for global syncs' : (isVerified ? 'Customer is already verified' : 'Verify customer')}
                  </div>
                }
              >
                <Button
                  aria-label="Verify"
                  className="verify-button"
                  icon={<CheckmarkCircleRegular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVerifyCustomer(item.CustomerID);
                  }}
                  disabled={isGlobalSyncs || isVerified || isDeleted}
                />
              </Tooltip>
              <Tooltip
                relationship="description"
                content={
                  <div style={{
                    backgroundColor: '#323130',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    {isGlobalSyncs ? 'Not available for global syncs' : (isDeleted ? 'Restore deleted customer' : 'Customer is not deleted')}
                  </div>
                }
              >
                <Button
                  aria-label="Undelete"
                  className="undelete-button"
                  icon={<ArrowUndoRegular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUndeleteCustomer(item.CustomerID);
                  }}
                  disabled={isGlobalSyncs || !isDeleted}
                />
              </Tooltip>
              <Tooltip
                relationship="description"
                content={
                  <div style={{
                    backgroundColor: '#323130',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    {isGlobalSyncs ? 'Not available for global syncs' : (isDeleted ? 'Customer is already deleted' : 'Delete customer')}
                  </div>
                }
              >
                <Button
                  aria-label="Delete"
                  className="delete-button"
                  icon={<DeleteRegular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCustomer(item.CustomerID);
                  }}
                  disabled={isGlobalSyncs || isDeleted}
                />
              </Tooltip>
            </div>
          </TableCellLayout>
        );
      }
    })
  ];

  return (
    <div style={{ margin: '20px' }}>
      <style>{buttonStyles}</style>
      <h1>Customers</h1>
      <Link 
        to="/" 
        style={{ 
          display: 'inline-block', 
          marginBottom: '20px', 
          padding: '8px 16px', 
          backgroundColor: '#0078d4', 
          color: 'white', 
          textDecoration: 'none', 
          borderRadius: '4px',
          fontSize: '14px'
        }}
      >
        Back to Home
      </Link>

      <div style={{ 
        marginBottom: '20px', 
        marginTop: '20px'
      }}>
        <SearchBox
          type="search"
          size="medium"
          appearance="outline"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by Company Name or Email"
          className="search-box-underline"
          style={{ 
            maxWidth: '400px',
            width: '100%'
          }}
        />
      </div>

      {loading ? (
        <p style={{ fontSize: '16px', marginTop: '20px' }}>Loading customers...</p>
      ) : filteredCustomers.length === 0 ? (
        <p style={{ fontSize: '16px', marginTop: '20px' }}>
          {searchTerm ? 'No customers found matching your search.' : 'No customers found.'}
        </p>
      ) : (
        <div style={{ 
          border: '1px solid #e1dfdd', 
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <DataGrid
            items={filteredCustomers}
            columns={columns}
            sortable
            noNativeElements={false}
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'auto'
            }}
          >
            <DataGridHeader>
              <DataGridRow style={{ borderBottom: '2px solid #e1dfdd' }}>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell
                    style={{
                      borderRight: '1px solid #e1dfdd',
                      padding: '2px 4px',
                      backgroundColor: '#f3f2f1',
                      fontWeight: '600'
                    }}
                  >
                    {renderHeaderCell()}
                  </DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody>
              {({ item, rowId }) => (
                <DataGridRow 
                  key={rowId}
                  className={!item.TenantInt ? 'non-clickable-row' : ''}
                  style={{ 
                    borderBottom: '1px solid #e1dfdd', 
                    cursor: item.TenantInt ? 'pointer' : 'default'
                  }}
                  onClick={item.TenantInt ? () => navigate(`/sync-details/${item.TenantInt}/${item.CustomerID}`) : undefined}
                >
                  {({ renderCell }) => (
                    <DataGridCell
                      style={{
                        borderRight: '1px solid #e1dfdd',
                        padding: '2px 4px'
                      }}
                    >
                      {renderCell(item)}
                    </DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        </div>
      )}
    </div>
  );
}

export default Customers;
